import { useState, useRef } from 'react';
import { initializeChakraComponent } from 'Shared/chakra-initialize';
import {
   Box,
   Button,
   Flex,
   FormControl,
   FormHelperText,
   FormLabel,
   Heading,
   Input,
   InputGroup,
   InputProps,
   InputRightElement,
   Link,
   VStack,
   VisuallyHidden,
} from '@chakra-ui/react';
import { FaIcon } from '@ifixit/icons';
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import { _js } from '@ifixit/localize';
import IFixitLogo from 'Shared/icons/logos/ifixit.svg';
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { Recaptcha } from 'Shared/recaptcha';
import { Auth } from 'Shared/auth';
import { fireGlobal, goToLastPage } from 'Shared/utils';
import { StatusPanel } from 'Shared/status_panel';
import { SSOLoginButtons } from '../SSOLoginButtons';
import { SentryError } from '@ifixit/sentry';

function LogInPasswordInput(props: InputProps) {
   const [show, setShow] = useState(false);
   const handleClick = () => setShow(!show);

   return (
      <InputGroup>
         <Input
            id="password"
            autoComplete="current-password"
            pr={5}
            type={show ? 'text' : 'password'}
            {...props}
         />
         <InputRightElement boxSize="36px">
            <Button
               variant="unstyled"
               onClick={handleClick}
               display="inline-flex"
               alignItems="center"
            >
               <FaIcon icon={show ? faEyeSlash : faEye} boxSize="16px" color="gray.400" />
            </Button>
         </InputRightElement>
      </InputGroup>
   );
}

export const getErrorMessage = (response: any) => {
   switch (response.reason) {
      case 'email': {
         return App.loginMessages.invalid_email;
      }
      case 'password': {
         return App.loginMessages.invalid_password;
      }
      case 'invalid_code': {
         return App.loginMessages.invalid_code;
      }
      case 'banned': {
         return App.loginMessages.banned;
      }
      case 'captcha': {
         return App.loginMessages.captcha;
      }
      case 'tooManyFailures': {
         const expiration = Number.parseInt(response.lockedOut, 10);
         const xMessage =
            expiration == 1
               ? _js('Wait one minute to try again.')
               : _js('Wait a little while before trying again.');
         const message = App.loginMessages.tooManyFailures + ' ' + xMessage;

         return message.replace('%1', expiration.toString());
      }
      case 'tooManyCodeAttempts': {
         return App.loginMessages.tooManyCodeAttempts;
      }
      case 'tooManyCodeGenerations': {
         return App.loginMessages.tooManyCodeGenerations;
      }
      case 'tooManySuccesses': {
         const expiration = Number.parseInt(response.lockedOut, 10);
         const xMessage =
            expiration == 1
               ? _js('Wait one minute to try again.')
               : _js('Wait %1 minutes to try again.');
         const message = App.loginMessages.tooManySuccesses + ' ' + xMessage;
         return message.replace('%1', expiration.toString());
      }
      case 'disabledUser': {
         return App.loginMessages.disabledUser;
      }
      case 'insecurePassword': {
         return App.loginMessages.insecurePassword;
      }
      case 'facebook': {
         return _js(
            'Please login using the "Sign in with Facebook" button instead, or remove your Facebook connection by resetting your password.'
         );
      }
      case 'OAuth2_GOOGLE': {
         return _js(
            'Please login using the "Sign in with Google" button instead, or remove your Google connection by resetting your password.'
         );
      }
      case 'adminOnlyEntrypoint': {
         return App.loginMessages.adminOnlyEntrypoint;
      }
      default: {
         return response.reason;
      }
   }
};

export const LogIn = () => {
   const [step, setStep] = useState('email');
   const [userEmail, setUserEmail] = useState('');
   const [codeSent, setCodeSent] = useState(false);
   const [isNewUser, setIsNewUser] = useState(false);
   const [apiError, setApiError] = useState<string | null>(null);

   const recaptchaPlaceholderRef = useRef<HTMLDivElement>(null);

   const handleSubmit = (
      values: { password: string },
      { setSubmitting }: FormikHelpers<{ password: string }>
   ) => {
      // Clear any previous errors
      setApiError(null);

      const loginHandler = (response: any) => {
         (Auth as any).clearCookies();
         (StatusPanel as any).deactivate(/* noDelay */ true);

         if (response.loggedIn) {
            fireGlobal('login.success', response);
         } else {
            setApiError(getErrorMessage(response));
            fireGlobal('login.failure', response);
         }
      };

      const loginPromise = Recaptcha.executeInvisible(
         recaptchaPlaceholderRef.current,
         App.recaptchaSiteKey,
         'login'
      )
         .then((recaptchaResponse: any) =>
            (Auth as any).sendLogin({
               login: userEmail,
               password: values.password,
               captchaResult: recaptchaResponse,
               ajax: true,
               handler: loginHandler,
            })
         )
         .then((result: any) => {
            setSubmitting(false);
            if (!result.loggedIn) {
               throw undefined;
            }
            goToLastPage();
         });

      LoadingIndicator.withPromise(loginPromise, {
         pendingMessage: _js('Checking your email and password...'),
         successMessage: _js('Login successful'),
         failureMessage: _js('Login unsuccessful'),
      });
   };

   const handleEmailSubmit = async (
      values: { email: string },
      { setSubmitting }: FormikHelpers<{ email: string }>
   ) => {
      setUserEmail(values.email);
      // Clear any previous errors when moving to a new step
      setApiError(null);

      // Validate email against the API
      await (Auth as any).validateLogin(values.email, async (response: any) => {
         if (response.hasAccount) {
            // Check if account is associated with an SSO provider
            if (response.ssoAssociation) {
               // If the account is associated with an SSO provider, they don't have
               // a password, so send them a one-time code.
               await requestCode(values.email);
            } else {
               // Email exists and no SSO association, proceed to auth step
               setStep('auth');
            }
         } else {
            setApiError(getErrorMessage({ reason: 'email' }));
         }
      });

      setSubmitting(false);
   };

   const requestCode = async (email: string) => {
      // Clear any previous errors
      setApiError(null);

      const formValues = {
         login: email,
         captchaResult: null,
      };

      const loginCodeHandler = (response: any) => {
         if (response.emailSent) {
            setCodeSent(true);
            setStep('code');
            setIsNewUser(response.newUser);
         } else {
            setApiError(getErrorMessage(response));
         }
      };

      // Get captcha token if configured
      if (App.recaptchaSiteKey) {
         const recaptchaResponse = await Recaptcha.executeInvisible(
            recaptchaPlaceholderRef.current,
            App.recaptchaSiteKey,
            'login_code'
         );
         formValues.captchaResult = recaptchaResponse;
      }

      await (Auth as any).sendGetLoginCode(formValues, loginCodeHandler);
   };

   const handleRequestCode = async () => {
      await requestCode(userEmail);
   };

   // Create a ref for the code input field
   const codeInputRef = useRef<HTMLInputElement>(null);

   const handleCodeSubmit = (
      values: { code: string },
      { setSubmitting, resetForm }: FormikHelpers<any>
   ) => {
      // Clear any previous errors
      setApiError(null);

      const loginHandler = (response: any) => {
         (Auth as any).clearCookies();
         (StatusPanel as any).deactivate(/* noDelay */ true);

         if (response.loggedIn) {
            fireGlobal('login.success', response);
         } else {
            if (response.reason === 'password') {
               response.reason = 'invalid_code';
            }
            setApiError(getErrorMessage(response));
            // Clear the code field on failure so user can try again
            resetForm({ values: { code: '' } });
            // Focus the input field
            setTimeout(() => {
               if (codeInputRef.current) {
                  codeInputRef.current.focus();
               }
            }, 0);
            fireGlobal('login.failure', response);
         }
      };

      const loginPromise = (Auth as any)
         .sendLoginWithCode(
            {
               login: userEmail,
               code: values.code,
            },
            loginHandler
         )
         .then((result: any) => {
            setSubmitting(false);
            if (!result?.loggedIn) {
               throw new SentryError('Login failed');
            }
            goToLastPage();
         });

      LoadingIndicator.withPromise(loginPromise, {
         pendingMessage: _js('Verifying code...'),
         successMessage: _js('Login successful'),
         failureMessage: _js('Code verification failed'),
      });
   };

   const [ssoError, setSsoError] = useState<string | null>(null);
   const ssoLoginHandler = (response: any) => {
      (Auth as any).clearCookies();
      (StatusPanel as any).deactivate(/* noDelay */ true);

      if (response.loggedIn) {
         fireGlobal('login.success', response);
         goToLastPage();
      } else {
         setSsoError(getErrorMessage(response));
         fireGlobal('login.failure', response);
      }
   };

   if (step === 'email') {
      return (
         <Box fontSize="16px" width={{ base: '100%', sm: '384px' }}>
            <VStack spacing={6} marginBottom={8}>
               <IFixitLogo />
               <Heading
                  as="h2"
                  fontSize={{ base: '24px', sm: '30px' }}
                  fontWeight="medium"
                  border="none"
                  textAlign="center"
                  lineHeight="normal"
               >
                  {_js('Sign in to your account')}
               </Heading>
            </VStack>
            {App.showJoinLink && (
               <Flex justify="center" marginBottom={4}>
                  <Box as="span">{_js('New?')}</Box>
                  &nbsp;
                  <Link href="/Join" color="brand.500" fontWeight="medium">
                     {_js('Join us')}
                  </Link>
                  .
               </Flex>
            )}
            <VStack
               className="sign-up-wrapper"
               flexDirection="column"
               spacing={6}
               paddingY={8}
               paddingX={6}
               bgColor="white"
               borderWidth={1}
               borderColor="gray.300"
               borderRadius="base"
            >
               <SSOLoginButtons
                  loginHandler={ssoLoginHandler}
                  error={ssoError}
                  setError={setSsoError}
               />
               <Box className="sign-up-form" width="100%">
                  {apiError && (
                     <Box
                        mb={4}
                        color="red.500"
                        textAlign="center"
                        id="api-error"
                        data-testid="api-error"
                     >
                        {apiError}
                     </Box>
                  )}
                  <Formik initialValues={{ email: '' }} onSubmit={handleEmailSubmit}>
                     {({ isSubmitting }) => (
                        <Form>
                           <VStack spacing={6}>
                              <Field name="email">
                                 {({ field }: FieldProps) => (
                                    <FormControl>
                                       <FormLabel>{_js('Email')}</FormLabel>
                                       <Input
                                          id="email"
                                          type="email"
                                          placeholder="albert@domain.com"
                                          autoComplete="username"
                                          required
                                          {...field}
                                          value={field.value || ''}
                                       />
                                    </FormControl>
                                 )}
                              </Field>
                              <Button
                                 id="continueBtn"
                                 variant="solid"
                                 colorScheme="blue"
                                 fontWeight="semibold"
                                 width="full"
                                 type="submit"
                                 isDisabled={isSubmitting}
                                 isLoading={isSubmitting}
                              >
                                 {_js('Continue')}
                              </Button>
                           </VStack>
                        </Form>
                     )}
                  </Formik>
               </Box>
            </VStack>
         </Box>
      );
   }

   if (step === 'code') {
      return (
         <Box fontSize="16px" width={{ base: '100%', sm: '384px' }}>
            <VStack spacing={6} marginBottom={8}>
               <IFixitLogo />
               <Heading
                  as="h2"
                  fontSize={{ base: '24px', sm: '30px' }}
                  fontWeight="medium"
                  border="none"
                  textAlign="center"
                  lineHeight="normal"
               >
                  {isNewUser ? _js('Create your account') : _js('Enter verification code')}
               </Heading>
               <Box textAlign="center" color="gray.600">
                  {userEmail}
                  <Button
                     variant="link"
                     marginLeft={2}
                     color="blue.500"
                     onClick={() => setStep('email')}
                  >
                     ({_js('change')})
                  </Button>
               </Box>
               {codeSent && (
                  <Box textAlign="center" color="gray.600">
                     {_js('We sent a verification code to your email')}
                  </Box>
               )}
            </VStack>
            <VStack
               className="sign-up-wrapper"
               flexDirection="column"
               spacing={6}
               paddingY={8}
               paddingX={6}
               bgColor="white"
               borderWidth={1}
               borderColor="gray.300"
               borderRadius="base"
            >
               <Box className="sign-up-form" width="100%">
                  {apiError && (
                     <Box
                        mb={4}
                        color="red.500"
                        textAlign="center"
                        id="api-error"
                        data-testid="api-error"
                     >
                        {apiError}
                     </Box>
                  )}
                  <Formik initialValues={{ code: '' }} onSubmit={handleCodeSubmit}>
                     <Form>
                        <VStack spacing={6}>
                           <Field name="code">
                              {({ field, form }: FieldProps) => (
                                 <FormControl>
                                    <FormLabel>{_js('Enter 6-digit code')}</FormLabel>
                                    <Input
                                       {...field}
                                       id="login-code"
                                       value={field.value || ''}
                                       type="text"
                                       maxLength={6}
                                       pattern="[0-9]*"
                                       inputMode="numeric"
                                       placeholder="000000"
                                       autoComplete="one-time-code"
                                       onChange={async e => {
                                          const value = e.target.value.replace(/[^0-9]/g, '');
                                          await form.setFieldValue('code', value);
                                       }}
                                    />
                                    <FormHelperText>
                                       {_js('Check your email for the verification code')}
                                    </FormHelperText>
                                 </FormControl>
                              )}
                           </Field>
                           {App.recaptchaSiteKey && (
                              <Box
                                 ref={recaptchaPlaceholderRef}
                                 className="showLogin js-captcha-container captcha-container"
                              />
                           )}
                           <Button
                              id="verifyBtn"
                              variant="solid"
                              colorScheme="blue"
                              fontWeight="semibold"
                              width="full"
                              type="submit"
                           >
                              {isNewUser ? _js('Create account') : _js('Sign in')}
                           </Button>
                           <Box textAlign="center">
                              <Button
                                 variant="link"
                                 color="blue.500"
                                 onClick={handleRequestCode}
                                 disabled={!codeSent}
                              >
                                 {_js('Send new code')}
                              </Button>
                           </Box>
                        </VStack>
                     </Form>
                  </Formik>
               </Box>
            </VStack>
         </Box>
      );
   }

   return (
      <Box fontSize="16px" width={{ base: '100%', sm: '384px' }}>
         <VStack spacing={6} marginBottom={8}>
            <IFixitLogo />
            <Heading
               as="h2"
               fontSize={{ base: '24px', sm: '30px' }}
               fontWeight="medium"
               border="none"
               textAlign="center"
               lineHeight="normal"
            >
               {_js('Sign in to your account')}
            </Heading>
            <Box textAlign="center" color="gray.600">
               {userEmail}
               <Button
                  variant="link"
                  marginLeft={2}
                  color="blue.500"
                  onClick={() => setStep('email')}
               >
                  ({_js('change')})
               </Button>
            </Box>
         </VStack>
         <VStack
            className="sign-up-wrapper"
            flexDirection="column"
            spacing={6}
            paddingY={8}
            paddingX={6}
            bgColor="white"
            borderWidth={1}
            borderColor="gray.300"
            borderRadius="base"
         >
            <Box className="sign-up-form" width="100%">
               {apiError && (
                  <Box
                     mb={4}
                     color="red.500"
                     textAlign="center"
                     id="api-error"
                     data-testid="api-error"
                  >
                     {apiError}
                  </Box>
               )}
               <Formik initialValues={{ password: '' }} onSubmit={handleSubmit}>
                  {({ isSubmitting }) => (
                     <Form>
                        <VStack spacing={6}>
                           {/* hidden username input to help password managers */}
                           <VisuallyHidden>
                              <Input readOnly value={userEmail} autoComplete="username" />
                           </VisuallyHidden>
                           <Field name="password">
                              {({ field }: FieldProps) => (
                                 <FormControl>
                                    <FormLabel>{_js('Password')}</FormLabel>
                                    <LogInPasswordInput {...field} value={field.value || ''} />
                                 </FormControl>
                              )}
                           </Field>
                           {App.recaptchaSiteKey && (
                              <Box
                                 ref={recaptchaPlaceholderRef}
                                 className="showLogin js-captcha-container captcha-container"
                              />
                           )}
                           <Button
                              id="loginBtn"
                              variant="solid"
                              colorScheme="blue"
                              fontWeight="semibold"
                              width="full"
                              type="submit"
                              isDisabled={isSubmitting}
                           >
                              {_js('Sign in with password')}
                           </Button>
                           <Box color="gray.500" marginY={4} textAlign="center">
                              {_js('or')}
                           </Box>
                           <Button
                              id="requestCodeLink"
                              variant="link"
                              color="blue.500"
                              fontWeight="medium"
                              onClick={handleRequestCode}
                              isDisabled={isSubmitting}
                           >
                              {_js('Email me a one-time login code')}
                           </Button>
                        </VStack>
                     </Form>
                  )}
               </Formik>
            </Box>
         </VStack>
      </Box>
   );
};

initializeChakraComponent('LogIn', LogIn);
